import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from '../ui/Tooltip';
import { cx } from '../ui/classNames';

/**
 * Tooltip copy used on both the cell pill and the popover header pill
 * (Trevor 2026-08-01: "Current price versus previous day close").
 */
export const MOVE_PILL_TOOLTIP = 'Current price versus previous day close';

/**
 * The colour band for a signed return_pct: its `direction` ('up', 'down' or
 * 'flat' for exactly 0) and `strength` ('strong' when |pct| reaches the
 * threshold, else 'muted').
 *
 * Bands (from Trevor's W7 spec, unchanged in W7.1 and W7.2):
 *   - `0` → neutral gray background.
 *   - `0 < |x| < threshold` → dull success / danger (55% of the colour).
 *   - `|x| >= threshold` → the bright colour.
 *
 * `threshold` defaults to 2.0 (matches the backend default) but callers pass
 * the server-echoed `mover_threshold_pct` so the bands stay in sync if the
 * env var is reconfigured.
 */
export function moveBand(pct, threshold = 2.0) {
    const direction = pct > 0 ? 'up' : (pct < 0 ? 'down' : 'flat');
    const strength = Math.abs(pct) >= threshold ? 'strong' : 'muted';
    return { direction, strength };
}

// The theme's semantic success and danger foregrounds (saturated, readable
// under white text) and its muted ink for no move: the colours the app
// themes' MSI semantic tones give.
const BAND_CLASS_NAMES = Object.freeze({
    up: {
        strong: 'bg-ui-success',
        muted: 'bg-[color-mix(in_srgb,var(--app-success)_55%,transparent)]',
    },
    down: {
        strong: 'bg-ui-danger',
        muted: 'bg-[color-mix(in_srgb,var(--app-danger)_55%,transparent)]',
    },
    flat: { strong: 'bg-ui-muted', muted: 'bg-ui-muted' },
});

// W8.1 (Trevor 2026-08-06): the compact pill is a little larger so the
// percent reads clearly in the cell overlay; with tenths precision the widest
// signed pill (`-15.1%`) still fits the 88-px `_newsCell` column.
const PILL_CLASS_NAME = 'inline-block whitespace-nowrap rounded-[7px] px-1 py-[1.2px] font-bold leading-[1.15] '
    + 'text-white [font-variant-numeric:tabular-nums]';
const DENSITY_CLASS_NAMES = Object.freeze({
    default: 'text-[0.7rem]',
    compact: 'text-[0.72rem]',
});

/**
 * Format a signed 1-dp percent string for display inside the pill.
 *
 * W8.1 (Trevor 2026-08-06 Slack): "the percent move was in tenths percentiles,
 * not hundredths. We should change to tenths — will simplify." Dropped from
 * hundredths (`+3.20%`) to tenths (`+3.2%`) so the pill reads cleaner at the
 * compact density and matches the visual spec.
 * Negatives already carry the '-' sign, so we only prepend '+' for positives.
 */
export function formatMovePct(pct) {
    const sign = pct > 0 ? '+' : '';
    return `${sign}${pct.toFixed(1)}%`;
}

/**
 * Solid-color pill rendering a signed percent move number.
 *
 * Used in two places (Trevor 2026-08-01 spec):
 *   1. `NewsCell.jsx` — the compact per-row watchlist pill, rendered as a
 *     top-right badge overlay on the news icon (W7.2 recomposition — pass
 *     `density="compact"` so it fits snugly inside the badge slot).
 *   2. `NewsPopover.jsx` — the header pill on the news popover (default
 *     density, roomier because the popover isn't width-constrained).
 *
 * Both sites use the same pill: the band colour from `moveBand` (exposed as
 * `data-direction` / `data-strength`), white text, small rounded corners and
 * compact padding. Tooltip copy is fixed to `MOVE_PILL_TOOLTIP` ("Current
 * price versus previous day close").
 *
 * Renders nothing when `pct` is null / undefined / non-finite — the caller
 * decides whether to render an em-dash placeholder in that slot.
 */
export default function MovePill({
    pct,
    threshold = 2.0,
    testId,
    density = 'default',
}) {
    const numeric = Number(pct);
    if (pct === null || pct === undefined || !Number.isFinite(numeric)) {
        return null;
    }
    const resolvedThreshold = Number.isFinite(Number(threshold))
        ? Number(threshold)
        : 2.0;
    const { direction, strength } = moveBand(numeric, resolvedThreshold);
    return (
        <Tooltip content={MOVE_PILL_TOOLTIP}>
            <span
                data-testid={testId}
                data-density={density}
                data-direction={direction}
                data-strength={strength}
                className={cx(
                    PILL_CLASS_NAME,
                    DENSITY_CLASS_NAMES[density] ?? DENSITY_CLASS_NAMES.default,
                    BAND_CLASS_NAMES[direction][strength],
                )}
            >
                {formatMovePct(numeric)}
            </span>
        </Tooltip>
    );
}

MovePill.propTypes = {
    pct: PropTypes.number,
    threshold: PropTypes.number,
    testId: PropTypes.string,
    density: PropTypes.oneOf(['default', 'compact']),
};
